import React, { useState, useEffect, useCallback } from 'react';
import { recommendationsService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { Ico, emo } from '../ui/Ico';

const BRAND  = '#D4A574';
const BRAND2 = '#b8895c';

/* ── Estilos compartilhados ──────────────────────────────────── */
const S = {
  card:    { background: '#ffffff', border: '1.5px solid #ebe3da', borderRadius: '14px', padding: '1.25rem 1.5rem', marginBottom: '1rem' },
  title:   { margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 600, color: '#2f2a26', display: 'flex', alignItems: 'center', gap: '0.4rem' },
  label:   { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#736b64', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:   { width: '100%', padding: '0.55rem 0.85rem', border: '1.5px solid #ebe3da', borderRadius: '9px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  select:  { width: '100%', padding: '0.55rem 0.85rem', border: '1.5px solid #ebe3da', borderRadius: '9px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#ffffff' },
  textarea:{ width: '100%', padding: '0.55rem 0.85rem', border: '1.5px solid #ebe3da', borderRadius: '9px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' },
  btn:     { padding: '0.55rem 1.2rem', background: BRAND, color: '#ffffff', border: 'none', borderRadius: '9px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' },
  btnOut:  { padding: '0.55rem 1.2rem', background: '#ffffff', color: BRAND, border: `1.5px solid ${BRAND}`, borderRadius: '9px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' },
  btnSm:   { padding: '0.35rem 0.75rem', background: BRAND, color: '#ffffff', border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' },
};

const WATER_OPTIONS = [
  'Menos de 1 litro',
  'Entre 1 e 1,5 litros',
  'Entre 1,5 e 2 litros',
  'Mais de 2 litros',
  'Livre demanda',
];

const FOODS_SUGGESTIONS = [
  'Produtos açucarados', 'Fast food', 'Frituras', 'Bebidas alcoólicas',
  'Embutidos', 'Refrigerantes', 'Gorduras saturadas', 'Ultraprocessados',
];

const OTHER_SUGGESTIONS = [
  'Comer lentamente, mastigando bem os alimentos',
  'Não pular refeições',
  'Evitar beliscar entre as refeições',
  'Respeitar os horários das refeições',
  'Ler rótulos dos alimentos',
  'Reduzir o consumo de sal',
];

const INTENSIDADE_OPTIONS = ['Leve', 'Moderada', 'Intensa'];

const emptyExercicio = () => ({ tipo: '', frequencia: '', duracao: '', intensidade: 'Moderada' });
const emptySuplem    = () => ({ nome: '', dose: '', frequencia: '', observacao: '' });

/* ── Modal genérico ──────────────────────────────────────────── */
function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={onClose}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{emo(title)}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#736b64', cursor: 'pointer', fontSize: '1.1rem' }}><Ico e="✕" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function TabRecomendacoes({ patientId }) {
  const { showSuccess, showError } = useToast();
  const [loading,  setLoading]   = useState(true);
  const [saving,   setSaving]    = useState(false);

  // Form state
  const [waterIntake,      setWaterIntake]      = useState('');
  const [foodsToAvoid,     setFoodsToAvoid]     = useState([]);
  const [supplements,      setSupplements]      = useState([]);
  const [physicalActivity, setPhysicalActivity] = useState([]);
  const [otherNotes,       setOtherNotes]       = useState('');

  // UI state
  const [foodInput,   setFoodInput]   = useState('');
  const [showExModal, setShowExModal] = useState(false);
  const [exForm,      setExForm]      = useState(emptyExercicio());
  const [showSupModal,setShowSupModal]= useState(false);
  const [supForm,     setSupForm]     = useState(emptySuplem());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await recommendationsService.getByPatient(patientId);
      if (res.success && res.data) {
        const d = res.data;
        setWaterIntake(d.water_intake || '');
        setFoodsToAvoid(d.foods_to_avoid || []);
        setSupplements(d.supplements || []);
        setPhysicalActivity(d.physical_activity || []);
        setOtherNotes(d.other_notes || '');
      }
    } catch { /* sem dados ainda */ }
    finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  /* ── Alimentos a evitar ── */
  const addFood = () => {
    const v = foodInput.trim();
    if (!v || foodsToAvoid.includes(v)) return;
    setFoodsToAvoid(prev => [...prev, v]);
    setFoodInput('');
  };
  const removeFood = (f) => setFoodsToAvoid(prev => prev.filter(x => x !== f));

  /* ── Exercícios ── */
  const saveExercicio = () => {
    if (!exForm.tipo.trim()) return;
    setPhysicalActivity(prev => [...prev, { ...exForm }]);
    setExForm(emptyExercicio()); setShowExModal(false);
  };
  const removeExercicio = (i) => setPhysicalActivity(prev => prev.filter((_, j) => j !== i));

  /* ── Suplementos ── */
  const saveSuplem = () => {
    if (!supForm.nome.trim()) return;
    setSupplements(prev => [...prev, { ...supForm }]);
    setSupForm(emptySuplem()); setShowSupModal(false);
  };
  const removeSuplem = (i) => setSupplements(prev => prev.filter((_, j) => j !== i));

  /* ── Salvar ── */
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await recommendationsService.save({
        patient_id:       patientId,
        water_intake:     waterIntake,
        foods_to_avoid:   foodsToAvoid,
        supplements,
        physical_activity: physicalActivity,
        other_notes:      otherNotes,
      });
      if (res.success) showSuccess('Recomendações salvas!');
      else showError(res.error || 'Erro ao salvar');
    } catch { showError('Erro ao salvar recomendações'); }
    finally { setSaving(false); }
  };

  /* ── PDF ── */
  const handleExportPDF = () => {
    const lines = [
      '=== RECOMENDAÇÕES NUTRICIONAIS ===\n',
      waterIntake ? `💧 Ingestão de Água: ${waterIntake}` : '',
      physicalActivity.length ? '\n🏃 Atividade Física:\n' + physicalActivity.map(e =>
        `  • ${e.tipo} — ${e.frequencia}x/sem, ${e.duracao}min, intensidade ${e.intensidade}`).join('\n') : '',
      foodsToAvoid.length ? '\n🚫 Alimentos a Evitar:\n' + foodsToAvoid.map(f => `  • ${f}`).join('\n') : '',
      supplements.length ? '\n💊 Suplementos:\n' + supplements.map(s =>
        `  • ${s.nome} ${s.dose} — ${s.frequencia}${s.observacao ? ' (' + s.observacao + ')' : ''}`).join('\n') : '',
      otherNotes ? `\n📝 Outras Recomendações:\n${otherNotes}` : '',
    ].filter(Boolean).join('\n');

    const blob = new Blob([lines], { type: 'text/plain;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = 'recomendacoes.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#736b64' }}>Carregando...</div>;

  const Tag = ({ text, onRemove, color = '#f8ebe9', borderColor = '#c46a60', textColor = '#a8483f' }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', background: color, border: `1px solid ${borderColor}`, color: textColor, borderRadius: '20px', padding: '0.2rem 0.6rem 0.2rem 0.75rem', fontSize: '0.82rem', fontWeight: 600 }}>
      {emo(text)}
      <button onClick={onRemove} style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer', fontSize: '0.85rem', lineHeight: 1, padding: 0, opacity: 0.7 }}><Ico e="✕" /></button>
    </span>
  );

  return (
    <div style={{ maxWidth: '720px' }}>

      {/* SEÇÃO 1 — Água */}
      <div style={S.card}>
        <h3 style={S.title}><Ico e="💧" sp />Ingestão de Água</h3>
        <label style={S.label}>Meta diária de hidratação</label>
        <select style={S.select} value={waterIntake} onChange={e => setWaterIntake(e.target.value)}>
          <option value="">Selecione...</option>
          {WATER_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>

      {/* SEÇÃO 2 — Atividade Física */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ ...S.title, margin: 0 }}><Ico e="🏃" sp />Atividade Física</h3>
          <button style={S.btnSm} onClick={() => { setExForm(emptyExercicio()); setShowExModal(true); }}>+ Adicionar exercício</button>
        </div>

        {physicalActivity.length === 0 ? (
          <p style={{ color: '#a39b93', fontSize: '0.85rem', margin: 0 }}>Nenhum exercício cadastrado.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {physicalActivity.map((ex, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.85rem', background: '#fbf8f5', border: '1.5px solid #ebe3da', borderRadius: '9px' }}>
                <span style={{ fontSize: '1rem' }}><Ico e="🏃" /></span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.87rem', color: '#2f2a26' }}>{ex.tipo}</div>
                  <div style={{ fontSize: '0.75rem', color: '#736b64', marginTop: '2px' }}>
                    {ex.frequencia && `${ex.frequencia}x/sem`}
                    {ex.duracao    && ` · ${ex.duracao}min`}
                    {ex.intensidade && ` · ${ex.intensidade}`}
                  </div>
                </div>
                <button onClick={() => removeExercicio(i)}
                  style={{ background: '#f8ebe9', border: 'none', color: '#c46a60', borderRadius: '7px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                  Remover
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SEÇÃO 3 — Alimentos a evitar */}
      <div style={S.card}>
        <h3 style={S.title}><Ico e="🚫" sp />Alimentos que devem ser evitados</h3>

        {/* Sugestões */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.85rem' }}>
          {FOODS_SUGGESTIONS.map(s => (
            <button key={s}
              onClick={() => { if (!foodsToAvoid.includes(s)) setFoodsToAvoid(prev => [...prev, s]); }}
              style={{ padding: '0.2rem 0.65rem', background: foodsToAvoid.includes(s) ? '#f8ebe9' : '#f6f0ea', border: `1px solid ${foodsToAvoid.includes(s) ? '#c46a60' : '#d9d0c6'}`, color: foodsToAvoid.includes(s) ? '#a8483f' : '#736b64', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
              {s}
            </button>
          ))}
        </div>

        {/* Campo livre */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.85rem' }}>
          <input style={{ ...S.input, flex: 1 }} value={foodInput} onChange={e => setFoodInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addFood()}
            placeholder="Adicionar outro alimento..." />
          <button style={S.btnSm} onClick={addFood}>Adicionar</button>
        </div>

        {/* Tags */}
        {foodsToAvoid.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {foodsToAvoid.map(f => (
              <Tag key={f} text={f} onRemove={() => removeFood(f)} />
            ))}
          </div>
        )}
      </div>

      {/* SEÇÃO 4 — Suplementos */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ ...S.title, margin: 0 }}><Ico e="💊" sp />Suplementos e Fitoterápicos</h3>
          <button style={S.btnSm} onClick={() => { setSupForm(emptySuplem()); setShowSupModal(true); }}>+ Adicionar suplemento</button>
        </div>

        {supplements.length === 0 ? (
          <p style={{ color: '#a39b93', fontSize: '0.85rem', margin: 0 }}>Nenhum suplemento cadastrado.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
            <thead>
              <tr>
                {['Suplemento', 'Dose', 'Frequência', 'Observação', ''].map(h => (
                  <th key={h} style={{ padding: '0.4rem 0.6rem', textAlign: 'left', background: '#fbf8f5', borderBottom: '1.5px solid #ebe3da', fontSize: '0.73rem', fontWeight: 600, color: '#736b64' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {supplements.map((s, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f6f0ea' }}>
                  <td style={{ padding: '0.5rem 0.6rem', fontWeight: 600, color: '#2f2a26' }}>{emo(s.nome)}</td>
                  <td style={{ padding: '0.5rem 0.6rem', color: '#4a4440' }}>{s.dose || '—'}</td>
                  <td style={{ padding: '0.5rem 0.6rem', color: '#4a4440' }}>{s.frequencia || '—'}</td>
                  <td style={{ padding: '0.5rem 0.6rem', color: '#736b64', fontStyle: 'italic' }}>{s.observacao || '—'}</td>
                  <td style={{ padding: '0.5rem 0.6rem' }}>
                    <button onClick={() => removeSuplem(i)}
                      style={{ background: 'none', border: 'none', color: '#e8c9a8', cursor: 'pointer', fontSize: '0.85rem' }}><Ico e="✕" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* SEÇÃO 5 — Outras recomendações */}
      <div style={S.card}>
        <h3 style={S.title}><Ico e="📝" sp />Outras Recomendações</h3>

        {/* Sugestões rápidas */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.85rem' }}>
          {OTHER_SUGGESTIONS.map(s => (
            <button key={s}
              onClick={() => setOtherNotes(prev => prev ? `${prev}\n• ${s}` : `• ${s}`)}
              style={{ padding: '0.2rem 0.65rem', background: '#f6f0ea', border: '1px solid #d9d0c6', color: '#736b64', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
              + {s}
            </button>
          ))}
        </div>

        <textarea style={{ ...S.textarea, minHeight: '120px' }} rows={5}
          value={otherNotes} onChange={e => setOtherNotes(e.target.value)}
          placeholder="Orientações gerais, hábitos e cuidados adicionais..." />
      </div>

      {/* Botões de ação */}
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginBottom: '2rem' }}>
        <button style={S.btnOut} onClick={handleExportPDF}><Ico e="📄" sp />Exportar PDF</button>
        <button style={S.btn} onClick={handleSave} disabled={saving}>
          {emo(saving ? 'Salvando...' : '💾 Salvar Recomendações')}
        </button>
      </div>

      {/* Modal Exercício */}
      {showExModal && (
        <Modal title="Adicionar Exercício" onClose={() => setShowExModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div>
              <label style={S.label}>Tipo de exercício *</label>
              <input style={S.input} value={exForm.tipo}
                onChange={e => setExForm(f => ({ ...f, tipo: e.target.value }))}
                placeholder="Ex: Caminhada, Musculação, Natação..." autoFocus />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={S.label}>Frequência (vezes/sem)</label>
                <input type="number" min="1" max="7" style={S.input}
                  value={exForm.frequencia}
                  onChange={e => setExForm(f => ({ ...f, frequencia: e.target.value }))}
                  placeholder="3" />
              </div>
              <div>
                <label style={S.label}>Duração (min)</label>
                <input type="number" min="1" style={S.input}
                  value={exForm.duracao}
                  onChange={e => setExForm(f => ({ ...f, duracao: e.target.value }))}
                  placeholder="45" />
              </div>
            </div>
            <div>
              <label style={S.label}>Intensidade</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {INTENSIDADE_OPTIONS.map(op => (
                  <button key={op} onClick={() => setExForm(f => ({ ...f, intensidade: op }))}
                    style={{ flex: 1, padding: '0.45rem', border: `1.5px solid ${exForm.intensidade === op ? BRAND : '#d9d0c6'}`, borderRadius: '8px', background: exForm.intensidade === op ? `${BRAND}18` : '#ffffff', color: exForm.intensidade === op ? BRAND2 : '#736b64', fontWeight: exForm.intensidade === op ? 700 : 500, cursor: 'pointer', fontSize: '0.82rem' }}>
                    {op}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
              <button style={S.btnOut} onClick={() => setShowExModal(false)}>Cancelar</button>
              <button style={S.btn} onClick={saveExercicio} disabled={!exForm.tipo.trim()}>Adicionar</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Suplemento */}
      {showSupModal && (
        <Modal title="Adicionar Suplemento" onClose={() => setShowSupModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div>
              <label style={S.label}>Nome do suplemento *</label>
              <input style={S.input} value={supForm.nome}
                onChange={e => setSupForm(f => ({ ...f, nome: e.target.value }))}
                placeholder="Ex: Vitamina D3, Ômega-3, Magnésio..." autoFocus />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={S.label}>Dose</label>
                <input style={S.input} value={supForm.dose}
                  onChange={e => setSupForm(f => ({ ...f, dose: e.target.value }))}
                  placeholder="Ex: 2000 UI, 1g" />
              </div>
              <div>
                <label style={S.label}>Frequência</label>
                <input style={S.input} value={supForm.frequencia}
                  onChange={e => setSupForm(f => ({ ...f, frequencia: e.target.value }))}
                  placeholder="Ex: 1x/dia, em jejum" />
              </div>
            </div>
            <div>
              <label style={S.label}>Observação</label>
              <input style={S.input} value={supForm.observacao}
                onChange={e => setSupForm(f => ({ ...f, observacao: e.target.value }))}
                placeholder="Ex: Tomar com refeição" />
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
              <button style={S.btnOut} onClick={() => setShowSupModal(false)}>Cancelar</button>
              <button style={S.btn} onClick={saveSuplem} disabled={!supForm.nome.trim()}>Adicionar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
